import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Dog, Cat, Cookie, Sparkles, Puzzle, ArrowRight, ExternalLink } from 'lucide-react';
import { EnhancedScrollReveal } from '../common/EnhancedScrollReveal';
import { Badge } from '../ui/Badge';
const categories = [{
  id: 'dog-food',
  name: 'Perros',
  description: 'Menús completos cocinados al vapor',
  icon: Dog,
  color: 'text-orange-600',
  bg: 'bg-orange-50',
  image: 'https://images.unsplash.com/photo-1589924691195-41432c84c161?auto=format&fit=crop&q=80&w=600',
  count: 24,
  startPrice: '2.50€',
  popular: true
}, {
  id: 'cat-food',
  name: 'Gatos',
  description: 'Recetas ricas en taurina y humedad',
  icon: Cat,
  color: 'text-blue-600',
  bg: 'bg-blue-50',
  image: 'https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?auto=format&fit=crop&q=80&w=600',
  count: 18,
  startPrice: '1.95€'
}, {
  id: 'snacks',
  name: 'Snacks',
  description: 'Premios 100% naturales deshidratados',
  icon: Cookie,
  color: 'text-amber-600',
  bg: 'bg-amber-50',
  image: 'https://images.unsplash.com/photo-1582798358481-d199fb7347bb?auto=format&fit=crop&q=80&w=600',
  count: 12,
  startPrice: '4.50€'
}, {
  id: 'care',
  name: 'Suplementos',
  description: 'Boosters de salud y bienestar',
  icon: Sparkles,
  color: 'text-emerald-600',
  bg: 'bg-emerald-50',
  image: 'https://images.unsplash.com/photo-1517662613602-4b8e02886677?auto=format&fit=crop&q=80&w=600',
  count: 8,
  startPrice: '12.00€',
  new: true
}, {
  id: 'toys',
  name: 'Juguetes',
  description: 'Estimulación mental y diversión',
  icon: Puzzle,
  color: 'text-purple-600',
  bg: 'bg-purple-50',
  image: 'https://images.unsplash.com/photo-1576201836106-db1758fd1c97?auto=format&fit=crop&q=80&w=600',
  count: 32,
  startPrice: '8.90€'
}];
export function ProductCategories() {
  return <section className="py-24 bg-white" data-id="element-2764">
      <div className="container-custom" data-id="element-2765">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6 border-b border-gray-100 pb-8" data-id="element-2766">
          <EnhancedScrollReveal data-id="element-2767">
            <span className="text-primary-600 font-bold tracking-wider uppercase text-xs mb-2 block" data-id="element-2768">
              Nuestro Catálogo
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-charcoal-900 mb-4" data-id="element-2769">
              Explora por Categoría
            </h2>
            <p className="text-charcoal-600 max-w-xl text-lg font-light" data-id="element-2770">
              Todo lo que necesitas para el bienestar integral de tu mascota,
              organizado para ti.
            </p>
          </EnhancedScrollReveal>

          <EnhancedScrollReveal delay={0.2} data-id="element-2771">
            <Link to="/productos" className="group flex items-center gap-2 font-bold text-charcoal-900 hover:text-primary-700 transition-colors border-b-2 border-transparent hover:border-primary-600 pb-0.5" data-id="element-2772">
              Ver todo el catálogo
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-2773" />
            </Link>
          </EnhancedScrollReveal>
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6" data-id="element-2774">
          {categories.map((category, index) => <EnhancedScrollReveal key={category.id} delay={index * 0.1} className="h-full" data-id="element-2775">
              <Link to={`/productos?category=${category.id}`} className="group block h-full relative overflow-hidden rounded-3xl bg-gray-50 transition-all duration-500 hover:-translate-y-2 hover:shadow-soft-xl border border-transparent hover:border-gray-100" data-id="element-2776">
                {/* Image Background Area */}
                <div className="absolute inset-0 h-[60%] overflow-hidden" data-id="element-2777">
                  <img src={category.image} alt={category.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-2778" />
                  <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-white/90" data-id="element-2779" />
                  <div className="absolute inset-0 bg-gradient-to-t from-white via-white/40 to-transparent" data-id="element-2780" />
                </div>

                {/* Content Card */}
                <div className="relative h-full flex flex-col pt-[50%] p-6" data-id="element-2781">
                  <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-5 shadow-sm border border-white/50 flex-1 flex flex-col transition-colors group-hover:bg-white" data-id="element-2782">
                    {/* Icon & Badge */}
                    <div className="flex justify-between items-start mb-4" data-id="element-2783">
                      <div className={`w-12 h-12 rounded-xl ${category.bg} flex items-center justify-center shadow-inner group-hover:scale-110 transition-transform duration-300`} data-id="element-2784">
                        <category.icon className={`w-6 h-6 ${category.color}`} data-id="element-2785" />
                      </div>
                      {(category.popular || category.new) && <Badge variant={category.popular ? 'primary' : 'secondary'} size="sm" className="shadow-sm" data-id="element-2786">
                          {category.popular ? 'Popular' : 'Nuevo'}
                        </Badge>}
                    </div>

                    <h3 className="text-xl font-bold text-charcoal-900 mb-2 font-serif group-hover:text-primary-700 transition-colors" data-id="element-2787">
                      {category.name}
                    </h3>

                    <p className="text-sm text-charcoal-500 mb-6 line-clamp-2 leading-relaxed" data-id="element-2788">
                      {category.description}
                    </p>

                    <div className="mt-auto pt-4 border-t border-gray-100 flex items-center justify-between text-xs font-medium" data-id="element-2789">
                      <span className="text-charcoal-400" data-id="element-2790">
                        {category.count} productos
                      </span>
                      <span className="flex items-center gap-1 text-charcoal-900 font-bold group-hover:text-primary-600 transition-colors" data-id="element-2791">
                        desde {category.startPrice}
                        <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-2792" />
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            </EnhancedScrollReveal>)}
        </div>
      </div>
    </section>;
}